// Cognito_amsAuth_Role
// Cognito_amsUnauth_Role
// Initialize the Amazon Cognito credentials provider
import React from "react";
import { ApolloClient, InMemoryCache, ApolloLink, Observable } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { ErrorLink } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import ActionCableProvider from "./ActionCableProvider";
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink";
import { introspectionQueryResultData } from "../constants/graphql/fragmentTypes";
import { isPublicPath } from "./publicPaths";
import { buildLoginRedirectUrl } from "../utils/authReturnTo";

const csrfToken = () => document.querySelector("meta[name=csrf-token]").getAttribute("content");

const currentUrl = window.location.protocol + "//" + window.location.host;

// Cookie (set by backend, httpOnly) rides the WebSocket upgrade handshake automatically —
// no JWT subprotocol injection needed.
const ActionCable = ActionCableProvider.createConnection();

const wsPrefix = currentUrl.match(/https/) ? "wss://" : "ws://";
const wsUrl = wsPrefix + currentUrl.split("//")[1];
const wsClient = ActionCable.createConsumer(`${wsUrl}/cable/`);
const wsLink = new ActionCableLink({ cable: wsClient });

const httpLink = new UploadHttpLink({
  uri: `${currentUrl}/graphql`,
  credentials: "same-origin",
});

const VERSION_BANNER_ACKNOWLEDGED_AT_KEY = "versionBannerAcknowledgedAt";
const VERSION_BANNER_PENDING_VERSION_KEY = "versionBannerPendingVersion";
const ACKNOWLEDGEMENT_WINDOW_MS = 8 * 60 * 60 * 1000;

const releaseNumber = version => {
  const match = version?.match(/^v(\d+)$/);
  return match ? Number(match[1]) : null;
};

const hasObservedTargetVersion = (observedVersion, targetVersion) => {
  const observedReleaseNumber = releaseNumber(observedVersion);
  const targetReleaseNumber = releaseNumber(targetVersion);

  if (observedReleaseNumber !== null && targetReleaseNumber !== null) {
    return observedReleaseNumber >= targetReleaseNumber;
  }

  return observedVersion === targetVersion;
};

const acknowledgeObservedVersion = serverVersion => {
  const pendingVersion = sessionStorage.getItem(VERSION_BANNER_PENDING_VERSION_KEY);

  if (!pendingVersion || !hasObservedTargetVersion(serverVersion, pendingVersion)) {
    return;
  }

  sessionStorage.setItem(VERSION_BANNER_ACKNOWLEDGED_AT_KEY, String(Date.now()));
  sessionStorage.removeItem(VERSION_BANNER_PENDING_VERSION_KEY);
};

const acknowledgedWithinWindow = () => {
  const acknowledgedAt = Number(sessionStorage.getItem(VERSION_BANNER_ACKNOWLEDGED_AT_KEY));
  return Number.isFinite(acknowledgedAt) && Date.now() - acknowledgedAt < ACKNOWLEDGEMENT_WINDOW_MS;
};

let knownAppVersion = null;
let versionChangedDispatched = false;

const versionCheckLink = new ApolloLink((operation, forward) => {
  if (!forward) {
    return null;
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (response) => {
        const context = operation.getContext();
        const serverVersion = context.response?.headers?.get("X-App-Version");

        if (serverVersion && serverVersion !== "dev") {
          acknowledgeObservedVersion(serverVersion);

          if (knownAppVersion && knownAppVersion !== serverVersion && !versionChangedDispatched && !acknowledgedWithinWindow()) {
            versionChangedDispatched = true;
            window.dispatchEvent(new CustomEvent("app-version-changed", { detail: { serverVersion } }));
          }

          knownAppVersion = serverVersion;
        }

        observer.next(response);
      },
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    });

    return () => subscription.unsubscribe();
  });
});

// Auth is carried by the httpOnly `jwt_token` cookie (sent automatically with
// `credentials: "same-origin"`). No Authorization header here.
const authMiddlewareLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      "X-CSRF-Token": csrfToken(),
    },
  });
  return forward(operation);
});

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      if (message === "User not signed in" && !isPublicPath(window.location.pathname)) {
        window.location.href = buildLoginRedirectUrl(window.location);
      }
    });
  } else {
    console.log(`[Network error]: ${error}`);
  }
});

const link = ApolloLink.split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  wsLink,
  httpLink
);

// Apollo Client v4 doesn't use IntrospectionFragmentMatcher anymore
// It uses possibleTypes directly in the cache configuration
const cache = new InMemoryCache({
  possibleTypes: introspectionQueryResultData.possibleTypes,
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, versionCheckLink, authMiddlewareLink, link]),
  cache: cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

export { client };

export default ({ children }) => <ApolloProvider client={client}>{children}</ApolloProvider>;
